// @ts-nocheck
/*
 * Bulk Create Extensions — v1 logic preserved end-to-end (Excel upload
 * with client-side preview + validation, default password fallback,
 * MAX_BULK_EXTENSIONS = 500 cap, downloadable template). Chrome swapped
 * to shadcn.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import {
  Info,
  ArrowLeft,
  Upload,
  Download,
  Loader2,
  FileSpreadsheet,
  Layers,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import type { ColumnDef } from '@tanstack/react-table';
import Loader from '@/components/partials/components/Loader';
import { DataTable } from '@/components/data-table/DataTable';
import { getApiCall, postApiCall, postApiMediaCall } from '@/service/httpCall';
import { API_EXTENSION_BULK_CREATE, API_USERS_PROFILE } from '@/service/url';
import { ROUTES, USER_ROLES } from '@/utilities/constant';
import { getUser } from '@/utilities/setting';
import { Button } from '@/components/ui/button';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';

const MAX_BULK_EXTENSIONS = 500;
const TEMPLATE_HEADERS = ['extension', 'password', 'description'];
const ENTERED_EXT_REGEX = /^[1-4][0-9]{0,3}$/;

const generateUsername = (companyCode, extensionCode, extensionInput) => {
  if (!companyCode || !extensionCode || !extensionInput) return '';
  return `${companyCode}${String(extensionCode).padStart(2, '0')}${extensionInput}`;
};

const getErrorMessage = (error, fallback) =>
  friendlyApiError(error) || fallback;

const normalizeHeader = (value) =>
  String(value || '').trim().toLowerCase().replace(/[\s_-]+/g, '');

const getCellValue = (row, keys) => {
  const normalizedRow = Object.entries(row || {}).reduce((acc, [key, value]) => {
    acc[normalizeHeader(key)] = value;
    return acc;
  }, {} as any);
  for (const key of keys) {
    const value = normalizedRow[normalizeHeader(key)];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }
  return '';
};

export default function BulkCreateExtension() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [companyCode, setCompanyCode] = useState('');
  const [rolePrefix, setRolePrefix] = useState('');
  const [defaultPassword, setDefaultPassword] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [previewRows, setPreviewRows] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<any>(null);

  useEffect(() => {
    const fetchCompanyContext = async () => {
      try {
        const data = getUser();
        if (!data?.id) {
          toast.error('User data not found. Please login again.');
          return;
        }
        const prefix =
          data.prefix === null || data.prefix === undefined || data.prefix === 0 ? 1 : Number(data.prefix);
        setRolePrefix(String(prefix).padStart(2, '0'));

        const isCallCenterAdmin =
          data?.role === USER_ROLES.CALL_CENTRE_ADMIN || data?.role === USER_ROLES.CALL_CENTRE_TEAM_LEAD;

        let companyCodeValue: any =
          data?.assignedCompany?.company_code ||
          data?.company?.company_code ||
          data?.assigned_company_details?.company_code ||
          null;

        if (isCallCenterAdmin && !companyCodeValue) {
          const currentProfileUrl = API_USERS_PROFILE.replace(':id', data.id);
          const currentProfile: any = await getApiCall(currentProfileUrl, {});
          const parentId = currentProfile?.parent_id || null;
          if (parentId) {
            const parentProfileUrl = API_USERS_PROFILE.replace(':id', parentId);
            const parentData: any = await getApiCall(parentProfileUrl, {});
            companyCodeValue =
              parentData?.assigned_company_details?.company_code ||
              parentData?.assignedCompany?.company_code ||
              parentData?.company?.company_code ||
              null;
          }
        }

        if (!companyCodeValue) {
          const currentProfileUrl = API_USERS_PROFILE.replace(':id', data.id);
          const currentProfile: any = await getApiCall(currentProfileUrl, {});
          companyCodeValue =
            currentProfile?.assigned_company_details?.company_code ||
            currentProfile?.assignedCompany?.company_code ||
            currentProfile?.company?.company_code ||
            null;
        }

        if (companyCodeValue) setCompanyCode(String(companyCodeValue).padStart(4, '0'));
        else toast.error('Company code not found. Please contact administrator.');
      } catch (err) {
        toast.error(getErrorMessage(err, 'Failed to load extension context.'));
      } finally {
        setLoading(false);
      }
    };
    fetchCompanyContext();
  }, []);

  const validPreviewRows = useMemo(() => previewRows.filter((row) => !row.error), [previewRows]);

  const parseExtensionValue = (rawExtension) => {
    const digits = String(rawExtension || '').replace(/\D/g, '');
    if (!digits) return { error: 'Extension is required' };
    const prefix = `${companyCode}${rolePrefix}`;
    if (digits.length <= 4) {
      if (!ENTERED_EXT_REGEX.test(digits)) {
        return { error: 'First digit must be 1-4 (not 0); remaining digits may be 0-9' };
      }
      return { extensionPart: digits, extension: `${prefix}${digits}` };
    }
    if (digits.length >= 7 && digits.length <= 10 && digits.startsWith(prefix)) {
      const enteredPart = digits.slice(prefix.length);
      if (!ENTERED_EXT_REGEX.test(enteredPart)) {
        return { error: 'Extension first digit must be 1-4 (not 0); remaining digits may be 0-9' };
      }
      return { extensionPart: enteredPart, extension: digits };
    }
    return { error: `Extension must be 1-4 digits, or full extension starting with ${prefix}` };
  };

  const buildPreviewRows = (rows, fallbackPassword = defaultPassword) =>
    rows.map((row, index) => {
      const rawExtension = getCellValue(row, ['extension', 'ext', 'extension_number', 'extension number']);
      const parsedExtension: any = parseExtensionValue(rawExtension);
      const rowPassword = getCellValue(row, ['password', 'pass']) || String(fallbackPassword || '').trim();
      const description = getCellValue(row, ['description', 'desc']);
      const errorMessages: string[] = [];
      if (parsedExtension.error) errorMessages.push(parsedExtension.error);
      if (!rowPassword || rowPassword.length < 4) errorMessages.push('Password must be at least 4 characters');
      return {
        key: index + 1,
        extensionPart: parsedExtension.extensionPart || rawExtension,
        extension: parsedExtension.extension || '',
        username: parsedExtension.extensionPart
          ? generateUsername(companyCode, rolePrefix, parsedExtension.extensionPart)
          : '',
        password: rowPassword,
        description,
        error: errorMessages.join('; '),
      };
    });

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    setResult(null);
    setPreviewRows([]);
    setUploadedFileName(file?.name || '');
    setUploadedFile(file || null);
    if (!file) return;
    if (!companyCode || !rolePrefix) {
      toast.error('Company code and extension code are required. Please refresh the page.');
      event.target.value = '';
      return;
    }
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const firstSheet = workbook.SheetNames[0];
      if (!firstSheet) return void toast.error('No sheet found in uploaded file.');
      const sheet = workbook.Sheets[firstSheet];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      if (!rows.length) return void toast.error('No data rows found in uploaded file.');
      if (rows.length > MAX_BULK_EXTENSIONS) {
        toast.error(`Cannot upload more than ${MAX_BULK_EXTENSIONS} extensions at once.`);
        return;
      }
      const parsedRows = buildPreviewRows(rows);
      setPreviewRows(parsedRows);
      const invalidCount = parsedRows.filter((row) => row.error).length;
      if (invalidCount > 0) {
        toast.warning(`${invalidCount} row(s) need correction before they can be created.`);
      } else {
        toast.success(`${parsedRows.length} row(s) loaded from file.`);
      }
    } catch {
      toast.error('Failed to read Excel file. Please check the template and try again.');
    } finally {
      event.target.value = '';
    }
  };

  const handleDefaultPasswordChange = (value) => {
    setDefaultPassword(value);
    if (previewRows.length) {
      setPreviewRows((prev) =>
        buildPreviewRows(
          prev.map((row) => ({
            extension: row.extensionPart,
            password: row.password || value,
            description: row.description,
          })),
          value,
        ),
      );
    }
  };

  const downloadTemplate = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      [
        { extension: '1001', password: '1234', description: 'Sales extension' },
        { extension: '1002', password: '1234', description: 'Support extension' },
      ],
      { header: TEMPLATE_HEADERS },
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Extensions');
    XLSX.writeFile(workbook, 'bulk-extension-template.xlsx');
  };

  const validateForm = () => {
    if (!companyCode || !rolePrefix) {
      toast.error('Company code and extension code are required. Please refresh the page.');
      return false;
    }
    if (!uploadedFile) return void toast.error('Please upload an Excel file first.') || false;
    if (previewRows.length === 0) return void toast.error('Please upload an Excel file first.') || false;
    if (previewRows.length > MAX_BULK_EXTENSIONS) {
      toast.error(`Cannot create more than ${MAX_BULK_EXTENSIONS} extensions at once.`);
      return false;
    }
    if (validPreviewRows.length === 0) return void toast.error('No valid rows to create.') || false;
    return true;
  };

  const handleBulkCreate = async () => {
    if (!validateForm()) return;
    setSubmitting(true);
    setResult(null);
    try {
      const extensions = validPreviewRows.map(({ extension, username, password, description }) => ({
        extension,
        username,
        password,
        description,
      }));

      const formData = new FormData();
      formData.append('file', uploadedFile as File);
      formData.append('extensions', JSON.stringify(extensions));

      const data: any = await postApiMediaCall(API_EXTENSION_BULK_CREATE, formData);
      setResult(data);
      if (data?.created_count > 0) toast.success(`${data.created_count} extension(s) created successfully`);
      if (data?.skipped_count > 0 || data?.failed_count > 0) {
        toast.warning('Some extensions were skipped or failed. Check the result below.');
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to create extensions.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Loader />;

  const previewColumns: ColumnDef<any>[] = [
    { accessorKey: 'key', header: '#' },
    { accessorKey: 'extensionPart', header: 'Entered Extension' },
    { accessorKey: 'extension', header: 'Full Extension' },
    { accessorKey: 'username', header: 'Username' },
    { accessorKey: 'description', header: 'Description' },
    { accessorKey: 'error', header: 'Validation' },
  ];

  const skippedFailedColumns: ColumnDef<any>[] = [
    { accessorKey: 'row', header: 'Row' },
    { accessorKey: 'extension', header: 'Extension' },
    { accessorKey: 'username', header: 'Username' },
    { accessorKey: 'reason', header: 'Reason' },
  ];

  return (
    <ScrollPage>
      <PageHeader
        title="Bulk Create Extensions"
        description="Upload an Excel file to provision extensions in bulk."
        actions={
          <>
            <Button variant="outline" onClick={downloadTemplate}>
              <Download className="h-4 w-4" />
              Download template
            </Button>
            <Button asChild variant="outline">
              <Link to={`/${ROUTES.MANAGE_AGENTS.EXTENSION}`}>
                <ArrowLeft className="h-4 w-4" />
                Back
              </Link>
            </Button>
          </>
        }
      />

      <div
        className="rounded-2xl border border-border/60 bg-card p-5"
        style={{
          boxShadow:
            '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
        }}
      >
        <div className="mb-4 flex items-center gap-3 border-b border-border/50 pb-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-inset ring-primary/25">
            <Layers className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-foreground">
              Bulk provision extensions
            </h3>
            <p className="text-[11px] text-muted-foreground">
              Upload up to {MAX_BULK_EXTENSIONS} extensions in a single file.
            </p>
          </div>
        </div>

        <div className="mb-4 flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900 dark:border-sky-900/40 dark:bg-sky-950/40 dark:text-sky-200">
          <Info className="mt-0.5 h-4 w-4 flex-none" />
          <div>
            Upload an Excel file with columns: <strong>extension</strong>,{' '}
            <strong>password</strong>, and optional <strong>description</strong>.
            Extension can be the short 1-4 digit value; the system will generate
            the full extension.
            <br />
            <strong>Note:</strong> first digit must be 1–4 (cannot be 0); next up
            to 3 digits can be 0–9 (e.g. 1099). Each extension must be unique.
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground">
              Company code
            </label>
            <input
              value={companyCode || 'Loading…'}
              readOnly
              disabled
              className="input-embossed h-10 w-full rounded-md px-3 font-mono text-sm tracking-wider opacity-70"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground">
              Extension code
            </label>
            <input
              value={rolePrefix || 'Loading…'}
              readOnly
              disabled
              className="input-embossed h-10 w-full rounded-md px-3 font-mono text-sm tracking-wider opacity-70"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground">
              Default password
            </label>
            <input
              type="password"
              value={defaultPassword}
              onChange={(e) => handleDefaultPasswordChange(e.target.value)}
              placeholder="Blank rows will inherit this"
              className="input-embossed h-10 w-full rounded-md px-3 text-sm"
            />
          </div>
        </div>

        <div className="mt-4 space-y-1.5">
          <label className="text-xs font-semibold text-foreground">
            Upload Excel file <span className="text-destructive">*</span>
          </label>
          <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border/60 bg-muted/30 px-4 py-8 text-center transition-colors hover:border-primary/40">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
              <FileSpreadsheet className="h-5 w-5" />
            </div>
            <div className="text-sm font-semibold text-foreground">
              {uploadedFileName || 'Drop or click to pick an Excel file'}
            </div>
            <p className="text-[11px] text-muted-foreground">
              Accepted: .xlsx, .xls, .csv · max {MAX_BULK_EXTENSIONS} rows
            </p>
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileUpload}
              className="hidden"
            />
          </label>
        </div>

        <div className="mt-5 flex items-center justify-end gap-2 border-t border-border/50 pt-4">
          <Button
            onClick={handleBulkCreate}
            disabled={submitting || validPreviewRows.length === 0}
            className="gap-1.5"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            {submitting
              ? 'Creating…'
              : validPreviewRows.length > 0
                ? `Create ${validPreviewRows.length} extension${validPreviewRows.length === 1 ? '' : 's'}`
                : 'Create extensions'}
          </Button>
        </div>
      </div>

      {previewRows.length > 0 && (
        <div className="mt-4">
          <h6 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Preview{' '}
            <span className="text-foreground">
              ({validPreviewRows.length} valid / {previewRows.length} total)
            </span>
          </h6>
          <DataTable
            tableId="bulk-ext-preview"
            columns={previewColumns}
            data={previewRows.slice(0, 100)}
            pageSize={10}
            emptyTitle="No rows"
            disableExport
          />
          {previewRows.length > 100 && (
            <p className="mt-1 text-[11px] text-muted-foreground">
              Showing first 100 preview rows only.
            </p>
          )}
        </div>
      )}

      {result && (
        <div
          className="mt-4 rounded-2xl border border-border/60 bg-card p-4"
          style={{
            boxShadow:
              '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
          }}
        >
          <h6 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Bulk create result
          </h6>
          <div className="mb-3 flex flex-wrap gap-1.5 text-[11px]">
            <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 font-semibold text-foreground ring-1 ring-inset ring-border/60">
              Requested: {result.requested_count || 0}
            </span>
            <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300">
              Created: {result.created_count || 0}
            </span>
            <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 font-semibold text-amber-700 ring-1 ring-inset ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300">
              Skipped: {result.skipped_count || 0}
            </span>
            <span className="inline-flex items-center rounded-full bg-rose-100 px-2.5 py-0.5 font-semibold text-rose-700 ring-1 ring-inset ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300">
              Failed: {result.failed_count || 0}
            </span>
          </div>
          {Array.isArray(result.skipped) && result.skipped.length > 0 && (
            <DataTable
              tableId="bulk-ext-skipped"
              columns={skippedFailedColumns}
              data={result.skipped}
              pageSize={10}
              emptyTitle="None"
              disableExport
            />
          )}
          {Array.isArray(result.failed) && result.failed.length > 0 && (
            <div className="mt-3">
              <DataTable
                tableId="bulk-ext-failed"
                columns={skippedFailedColumns}
                data={result.failed}
                pageSize={10}
                emptyTitle="None"
                disableExport
              />
            </div>
          )}
        </div>
      )}
    </ScrollPage>
  );
}
